import { useCallback, useState, useEffect } from "react";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  preparationVehicle,
  updateVehicleProps,
  getVehiclePreparationHistory,
} from "../../../../common/services/vehicles.api";
import { Modal } from "flowbite-react";
import PreparationVehicleCheck from "../forms/custom-fields/PreparationVehicleCheck";
import { IVehicle, PreparationImageData } from "../../../../types/vehicle.type";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";

import "./ModalPreparationVehicle.scss";

type PreparationVehicleProps = {
  formRequestErrors?: { message: string; path: string[] }[];
  vehicle: IVehicle;
  onClose: () => void;
  isReadOnly?: boolean;
  type?: string;
  workOrderId?: string;
};

interface FormikSubmitValues {
  vehiclePreparing: Omit<
    IVehicle["vehiclePreparing"],
    "preparation" | "repreparation"
  > & {
    preparation?: Record<string, PreparationImageData>;
    repreparation?: Record<string, PreparationImageData>;
    type?: string;
  };
}

export default function ModalPreparationVehicle(
  props: PreparationVehicleProps,
) {
  const isReadOnly = props.isReadOnly || false;
  const title = props.type === "repreparation" ? "Repreparando" : "Preparando";
  const { notifySuccess, notifyError } = useToastContext();
  const [, setNewInitialData] = useState({
    vehiclePreparing: {
      cellphoneSupport: false,
      engineProtection: false,
      paintingProtection: false,
      phoneChargerSupport: false,
      tracker: false,
    },
  });

  const validationSchema = Yup.object().shape({
    vehiclePreparing: Yup.object().shape({
      cellphoneSupport: Yup.boolean(),
      engineProtection: Yup.boolean(),
      paintingProtection: Yup.boolean(),
      phoneChargerSupport: Yup.boolean(),
      tracker: Yup.boolean(),
      trackerValidation: Yup.boolean(),
      hasSpareKey: Yup.boolean(),
      preparation: Yup.object(),
      preparator: Yup.string().required(
        "Selecionar um preparador é obrigatório",
      ),
    }),
  });

  const emptyObject = {
    paintingProtection: false,
    tracker: false,
    cellphoneSupport: false,
    phoneChargerSupport: false,
    engineProtection: false,
    trackerValidation: false,
    hasSpareKey: false,
  };

  const formik = useFormik({
    initialValues: {
      vehiclePreparing: {
        ...(props.vehicle.vehiclePreparing || emptyObject),
        preparation: {},
        preparator: props.vehicle.vehiclePreparing?.preparator || "",
        type: "preparation",
      },
    },
    validationSchema: validationSchema,
    onSubmit: async (data: FormikSubmitValues) => {
      let resultPrep;
      if (isVehiclePreparing(data)) {
        resultPrep = await preparationVehicle(
          props.vehicle.licensePlate,
          props.vehicle.model,
          {
            ...data.vehiclePreparing,
            type: "preparation",
          },
        );
      }

      const dataToUpdateVehicle = { ...data };
      if (dataToUpdateVehicle.vehiclePreparing) {
        delete dataToUpdateVehicle.vehiclePreparing.preparation;
        delete dataToUpdateVehicle.vehiclePreparing.repreparation;
        delete dataToUpdateVehicle.vehiclePreparing.type;
      }

      const result = await updateVehicleProps(
        props.vehicle._id as string,
        dataToUpdateVehicle as Partial<IVehicle>,
      );

      if (result.ok) {
        formik.setValues({
          ...result.data,
          vehiclePreparing: {
            ...result.data.vehiclePreparing,
            preparation: {},
          },
        } as unknown as FormikSubmitValues);
        setNewInitialData(result.data);
        if (resultPrep && !resultPrep.ok) {
          notifyError(resultPrep.message as string);
          props.onClose();
          return;
        }
        notifySuccess("atualizado com sucesso.");
        props.onClose();
      }
      return result;
    },
  });

  useEffect(() => {
    async function fetchHistoryImages() {
      try {
        const response = await getVehiclePreparationHistory(
          props.vehicle.licensePlate,
          "preparation",
          props.workOrderId,
        );
        if (response.ok && response.data && response.data.items) {
          formik.setFieldValue(
            "vehiclePreparing.preparation",
            response.data.items,
          );

          if (
            response.data.preparator &&
            formik.values.vehiclePreparing.preparator === ""
          ) {
            formik.setFieldValue(
              "vehiclePreparing.preparator",
              response.data.preparator,
            );
          }
        }
      } catch (error) {
        console.error("Erro ao buscar histórico de imagens", error);
      }
    }
    fetchHistoryImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.vehicle.licensePlate, props.workOrderId]);

  const isVehiclePreparing = useCallback((data = formik.values) => {
    if (
      [
        data.vehiclePreparing.cellphoneSupport,
        data.vehiclePreparing.engineProtection,
        data.vehiclePreparing.phoneChargerSupport,
        data.vehiclePreparing.paintingProtection,
        data.vehiclePreparing.tracker,
      ].every(Boolean)
    ) {
      return true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Modal size="5xl" show={true} onClose={() => props.onClose()}>
        <FormikProvider value={formik}>
          <Modal.Header className="text-black dark:text-black ">
            <h2 className="text-black dark:text-black">
              {title}: {props.vehicle.licensePlate}
            </h2>
          </Modal.Header>
          <Modal.Body className="w-full relative border-gray flex flex-col text-black">
            <section className="flex items-center justify-between p-4 dark:text-black">
              <PreparationVehicleCheck
                vehicleId={props.vehicle._id}
                isReadOnly={isReadOnly}
              />
            </section>
          </Modal.Body>
          <Modal.Footer className="text-black dark:text-black text-end w-full flex flex-col items-end">
            <label className="text-red-600 mb-2">
              {formik.values.vehiclePreparing.preparator === ""
                ? "Nenhum preparador selecionado"
                : ""}
            </label>
            {!isReadOnly && (
              <ButtonWithLoader
                isLoading={formik.isSubmitting}
                onClick={formik.handleSubmit}
              >
                Salvar
              </ButtonWithLoader>
            )}
          </Modal.Footer>
        </FormikProvider>
      </Modal>
    </>
  );
}
